import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Typography,
  Container,
  Card,
  CardContent,
  Chip,
  Alert,
  TextField,
  InputAdornment,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Help,
  Search,
  CheckCircle,
  Warning,
  Info,
  Support,
  Phone,
  Email,
  WhatsApp,
  Chat,
  Category,
  TrendingUp,
  Payment,
  Cancel,
  Security,
  ExpandMore
} from '@mui/icons-material';
import { helpCenterService } from '../services/firebase';
import { HelpCenterData } from '../types';
import { useTranslation } from 'react-i18next';
import { getTranslatedData } from '../utils/getTranslatedData';

// Icon mapping for dynamic icon rendering
const getIcon = (iconName: string) => {
  const iconProps = { color: 'primary' as const };
  
  switch (iconName) {
    case 'TrendingUp': return <TrendingUp {...iconProps} />;
    case 'Payment': return <Payment {...iconProps} />;
    case 'Cancel': return <Cancel {...iconProps} />;
    case 'Category': return <Category {...iconProps} />;
    case 'Phone': return <Phone />;
    case 'WhatsApp': return <WhatsApp />;
    case 'Email': return <Email />;
    case 'Chat': return <Chat />;
    case 'Security': return <Security {...iconProps} />;
    default: return <Support {...iconProps} />;
  }
};

// Alert type icon mapping
const alertIconMap: { [key: string]: React.ReactElement } = {
  info: <Info />,
  warning: <Warning />,
  success: <CheckCircle />,
  error: <Warning />
};

const HelpCenterPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [helpData, setHelpData] = useState<HelpCenterData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [expanded, setExpanded] = useState<string | false>(false);

  useEffect(() => {
    const fetchHelpData = async () => {
      try {
        setLoading(true);
        let data = await helpCenterService.getHelpCenterData();
        
        if (!data) {
          // Initialize with default data if no data exists
          await helpCenterService.initializeHelpCenterData();
          data = await helpCenterService.getHelpCenterData();
        }
        
        setHelpData(data);
      } catch (err: any) {
        setError('Yardım merkezi yüklenirken hata oluştu: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchHelpData();
  }, []);

  const handleChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <>
      <Helmet>
        <title>{getTranslatedData(helpData?.title) || t('static_pages.help_center')} - HatTravel</title>
        <meta name="description" content={getTranslatedData(helpData?.description) || t('static_pages.help_center_description')} />
      </Helmet>
      
      <Box sx={{ 
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        minHeight: '100vh'
      }}>
        {/* Hero Section */}
        <Box sx={{ 
          background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
          py: 8,
          mb: 6
        }}>
          <Container maxWidth="lg">
            <Box sx={{ textAlign: 'center' }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                <Box sx={{
                  width: 100,
                  height: 100,
                  borderRadius: '50%',
                  backgroundColor: '#005BBD',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 15px 35px rgba(0, 91, 189, 0.3)'
                }}>
                  <Help sx={{ fontSize: 50, color: 'white' }} />
                </Box>
              </Box>
              
              <Typography 
                variant="h1" 
                component="h1" 
                gutterBottom 
                sx={{ 
                  fontWeight: 200,
                  fontSize: { xs: '2.5rem', md: '4rem' },
                  background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 3
                }}
              >
                {getTranslatedData(helpData?.title) || t('static_pages.help_center')}
              </Typography>
              
              <Typography 
                variant="h5" 
                color="#7f8c8d" 
                sx={{ 
                  maxWidth: 700, 
                  mx: 'auto',
                  fontWeight: 300,
                  lineHeight: 1.6,
                  mb: 4
                }}
              >
                {getTranslatedData(helpData?.description) || 'Sorularınızın cevaplarını bulun veya bizimle iletişime geçin.'}
              </Typography>

              {/* Search Bar */}
              <Box sx={{ maxWidth: 600, mx: 'auto', mb: 4 }}>
                <TextField
                  fullWidth
                  placeholder={getTranslatedData(helpData?.searchPlaceholder) || 'Sorunuzu yazın veya anahtar kelime arayın...'}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ color: '#667eea' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <Button
                          variant="contained"
                          sx={{
                            background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
                            borderRadius: 2,
                            px: 3
                          }}
                        >
                          {t('help_center.search')}
                        </Button>
                      </InputAdornment>
                    )
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3,
                      background: 'rgba(255, 255, 255, 0.9)',
                      backdropFilter: 'blur(10px)',
                      '&:hover': {
                        background: 'rgba(255, 255, 255, 1)'
                      }
                    }
                  }}
                />
              </Box>

              {/* Quick Actions */}
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                <Chip
                  icon={<Phone />}
                  label={t('help_center.support_24_7')}
                  sx={{
                    background: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: 3,
                    fontWeight: 400
                  }}
                />
                <Chip
                  icon={<WhatsApp />}
                  label={t('help_center.whatsapp')}
                  sx={{
                    background: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: 3,
                    fontWeight: 400
                  }}
                />
                <Chip
                  icon={<Chat />}
                  label={t('help_center.live_chat')}
                  sx={{
                    background: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: 3,
                    fontWeight: 400
                  }}
                />
              </Box>
            </Box>
          </Container>
        </Box>

        <Container maxWidth="lg">
          {/* Important Notes */}
          {helpData?.importantNotes && helpData.importantNotes.length > 0 && (
            <Box sx={{ mb: 6 }}>
              {helpData.importantNotes
                ?.filter(note => note.isActive)
                ?.sort((a, b) => a.order - b.order)
                ?.map((note) => (
                  <Alert
                    key={note.id}
                    severity={note.type}
                    icon={alertIconMap[note.type]}
                    sx={{
                      mb: 2,
                      borderRadius: 3,
                      background: 'rgba(255, 255, 255, 0.9)',
                      backdropFilter: 'blur(10px)',
                      '& .MuiAlert-message': {
                        fontWeight: 300
                      }
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 400, mb: 1 }}>
                      {getTranslatedData(note.title)}
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 300 }}>
                      {getTranslatedData(note.content)}
                    </Typography>
                  </Alert>
                ))}
            </Box>
          )}

          {/* Support Methods */}
          {helpData?.supportMethods && helpData.supportMethods.length > 0 && (
            <Card sx={{ 
              background: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(10px)',
              borderRadius: 4,
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
              mb: 6
            }}>
              <CardContent sx={{ p: 5 }}>
                <Typography 
                  variant="h3" 
                  gutterBottom 
                  sx={{ 
                    fontWeight: 200,
                    background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: 4,
                    textAlign: 'center'
                  }}
                >
                  {t('help_center.support_channels')}
                </Typography>
                
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 3 }}>
                  {helpData.supportMethods
                    ?.filter(method => method.isActive)
                    ?.sort((a, b) => a.order - b.order)
                    ?.map((method) => (
                      <Card
                        key={method.id}
                        component="a"
                        href={method.contact.startsWith('http') ? method.contact : `mailto:${method.contact}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          p: 3,
                          textAlign: 'center',
                          borderRadius: 4,
                          textDecoration: 'none',
                          boxShadow: '0 8px 24px rgba(0,0,0,0.05)',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-5px)',
                            boxShadow: '0 12px 32px rgba(0,0,0,0.1)'
                          }
                        }}
                      >
                        <Box sx={{ mb: 2, color: method.color || 'primary.main', fontSize: 40 }}>
                          {getIcon(method.icon)}
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: 'var(--navy-blue)' }}>
                          {getTranslatedData(method.title)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {getTranslatedData(method.description)}
                        </Typography>
                      </Card>
                    ))}
                </Box>
              </CardContent>
            </Card>
          )}

          {/* FAQ Categories */}
          {helpData?.helpCategories && helpData.helpCategories.length > 0 && (
            <>
              {helpData.helpCategories
                ?.filter(category => category.isActive)
                ?.sort((a, b) => a.order - b.order)
                ?.map((category, categoryIndex) => (
                  <Card key={category.id} sx={{ 
                    background: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: 4,
                    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
                    mb: 4,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 25px 50px rgba(0, 0, 0, 0.15)',
                    }
                  }}>
                    <CardContent sx={{ p: 0 }}>
                      {/* Category Header */}
                      <Box sx={{ 
                        p: 4, 
                        background: `linear-gradient(135deg, ${category.color}15 0%, ${category.color}05 100%)`,
                        borderBottom: `2px solid ${category.color}20`
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                          <Box sx={{
                            width: 60,
                            height: 60,
                            borderRadius: '50%',
                            background: `linear-gradient(45deg, ${category.color} 30%, ${category.color}80 90%)`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: `0 8px 25px ${category.color}30`
                          }}>
                            {React.cloneElement(getIcon(category.icon), { 
                              sx: { fontSize: 28, color: 'white' } 
                            })}
                          </Box>
                          
                          <Typography 
                            variant="h3" 
                            sx={{ 
                              fontWeight: 200,
                              background: `linear-gradient(45deg, ${category.color} 30%, ${category.color}80 90%)`,
                              backgroundClip: 'text',
                              WebkitBackgroundClip: 'text',
                              WebkitTextFillColor: 'transparent'
                            }}
                          >
                            {getTranslatedData(category.title)}
                          </Typography>
                        </Box>
                      </Box>

                      {/* FAQ Items */}
                      <Box sx={{ p: 2 }}>
                        {category.questions
                          ?.filter(faq => faq.isActive)
                          ?.sort((a, b) => a.order - b.order)
                          ?.map((faq, faqIndex) => (
                            <Accordion
                              key={faq.id}
                              expanded={expanded === `panel${categoryIndex}-${faqIndex}`}
                              onChange={handleChange(`panel${categoryIndex}-${faqIndex}`)}
                              sx={{
                                background: 'transparent',
                                boxShadow: 'none',
                                '&:before': { display: 'none' },
                                '&:not(:last-child)': {
                                  borderBottom: '1px solid rgba(102, 126, 234, 0.1)'
                                }
                              }}
                            >
                              <AccordionSummary
                                expandIcon={<ExpandMore sx={{ color: category.color }} />}
                                sx={{
                                  '&:hover': {
                                    background: `${category.color}05`,
                                    borderRadius: 2
                                  },
                                  transition: 'all 0.3s ease'
                                }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                  <Support sx={{ color: category.color, fontSize: 20 }} />
                                  <Typography 
                                    variant="h6" 
                                    sx={{ 
                                      fontWeight: 400, 
                                      color: '#2c3e50',
                                      fontSize: '1.1rem'
                                    }}
                                  >
                                    {getTranslatedData(faq.question)}
                                  </Typography>
                                </Box>
                              </AccordionSummary>
                              
                              <AccordionDetails sx={{ pl: 6, pr: 4, pb: 3 }}>
                                <Typography 
                                  variant="body1" 
                                  color="#7f8c8d" 
                                  sx={{ 
                                    fontWeight: 300,
                                    lineHeight: 1.7,
                                    fontSize: '1rem'
                                  }}
                                >
                                  {getTranslatedData(faq.answer)}
                                </Typography>
                              </AccordionDetails>
                            </Accordion>
                          ))}
                      </Box>
                    </CardContent>
                  </Card>
                ))}
            </>
          )}

        <Box sx={{ height: 40 }} />
        </Container>
      </Box>
    </>
  );
};

export default HelpCenterPage; 